// Structured patient address -- the one place the app knows the six address
// fields, how they are shown, and how an ABHA snapshot maps onto them.
//
// formatAddressLines is the FE mirror of the backend `format_patient_address`
// (same rules, same shared test vectors); keep the two in step.
//
// Field names are PREFIXED `address_*` on purpose (D1): CrossTabSync strips
// keys by NAME recursively, so bare `state`/`district` would redact unrelated
// keys (the six names are added to CrossTabSync's SENSITIVE_FIELDS by FE-IPD).

import { INDIA_STATES, INDIA_STATE_ALIASES } from '../constants/indiaStates';

export const ADDRESS_FIELDS = [
  'address_line',
  'address_town',
  'address_sub_district',
  'address_district',
  'address_state',
  'address_pincode',
];

const PINCODE_RE = /^[0-9]{6}$/;

// Same text as the server's 422 (backend PINCODE_ERROR) -- one copy on the FE.
export const PINCODE_ERROR = 'Pincode must be 6 digits.';

const text = (value) => String(value ?? '').trim();
// Whole-part, case-insensitive key (backend: `text.casefold()` on the trimmed part).
const sameWordKey = (value) => value.toLowerCase();

/**
 * Two display lines for a patient's address, or null when there is nothing.
 *   line1 = address_line
 *   line2 = town, sub-district, district, state, then " - " + pincode
 *           (empty parts skipped; a repeated word shown once)
 * The legacy one-line `address` is used ONLY when all six fields are empty.
 */
export function formatAddressLines(patient) {
  const p = patient || {};
  const line1 = text(p.address_line);

  const seen = new Set();
  const places = [];
  ['address_town', 'address_sub_district', 'address_district', 'address_state'].forEach((field) => {
    const part = text(p[field]);
    const key = sameWordKey(part);
    if (part && !seen.has(key)) {
      seen.add(key);
      places.push(part);
    }
  });
  const place = places.join(', ');
  const pincode = text(p.address_pincode);
  const line2 = pincode ? (place ? `${place} - ${pincode}` : pincode) : place;

  if (line1 || line2) return { line1, line2 };

  // Reached only when all six fields are empty.
  const legacy = text(p.address);
  return legacy ? { line1: legacy, line2: '' } : null;
}

// ── Place-name normalisation: FE mirror of backend app/utils/india_states.py
// (title_case_place / canonical_state; same shared test vectors). The server
// stores ABHA places this way, so the form must hold the same spelling or an
// untouched field would compare (and be marked / PATCHed) as Edited.
const stateKey = (name) => name.toLowerCase().replace(/&/g, 'and').replace(/\s+/g, ' ').trim();
const STATE_BY_KEY = Object.freeze({
  ...Object.fromEntries(INDIA_STATES.map((s) => [stateKey(s), s])),
  ...INDIA_STATE_ALIASES,
});

/** All-UPPER / all-lower place name -> Title Case ("SATARA" -> "Satara"); mixed case kept. */
export function titleCasePlace(value) {
  const v = String(value ?? '').replace(/\s+/g, ' ').trim();
  if (v === v.toUpperCase() || v === v.toLowerCase()) {
    return v.replace(/[A-Za-z]+/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());
  }
  return v;
}

/** The canonical INDIA_STATES spelling of `value`; unknown -> titleCasePlace. */
export function canonicalState(value) {
  const key = stateKey(String(value ?? ''));
  return Object.prototype.hasOwnProperty.call(STATE_BY_KEY, key) ? STATE_BY_KEY[key] : titleCasePlace(value);
}

/**
 * The six address values carried by an ABHA profile snapshot
 * (`toABHAProfileSnapshot`: addressLine, townName, subdistrictName,
 * districtName, stateName, pincode) -- or null when ABHA has no usable
 * address part (null = nothing is replaced). Normalised exactly like the
 * server's `_abha_address_parts`: state -> canonicalState, town / sub-district
 * / district -> titleCasePlace, address line as-is; a pincode that is not
 * exactly 6 digits is dropped.
 */
export function abhaAddressValues(snapshot) {
  if (!snapshot) return null;
  const pincode = text(snapshot.pincode);
  const values = {
    address_line: text(snapshot.addressLine),
    address_town: titleCasePlace(text(snapshot.townName)),
    address_sub_district: titleCasePlace(text(snapshot.subdistrictName)),
    address_district: titleCasePlace(text(snapshot.districtName)),
    address_state: canonicalState(text(snapshot.stateName)),
    address_pincode: PINCODE_RE.test(pincode) ? pincode : '',
  };
  return ADDRESS_FIELDS.some((field) => values[field]) ? values : null;
}

/**
 * The ABHA address values to put into a form, or null to leave it alone.
 *   OTP create/verify snapshot -> abhaAddressValues (whole replace, OR-121).
 *   Scan & Share snapshot (`scanId`) -> fill ONLY when all six form fields
 *   are empty (OR-137: the server's claim writes the address only when the
 *   patient has none), else null.
 */
export function abhaAddressFill(snapshot, form) {
  const values = abhaAddressValues(snapshot);
  if (!values) return null;
  if (snapshot.scanId && ADDRESS_FIELDS.some((field) => text(form?.[field]))) return null;
  return values;
}

/**
 * True when the post-link address PATCH (D4) applies: an OTP link whole-replaces
 * the address, so staff edits are sent back once. A Scan & Share claim never
 * replaces a saved address (OR-137) -- the form's address was already saved at
 * registration -- so it never sends this PATCH.
 */
export function needsPostLinkAddressPatch(form, snapshot) {
  if (snapshot?.scanId) return false;
  return editedVsAbha(form, abhaAddressValues(snapshot));
}

/**
 * True when any of the six form values differs (trimmed) from the ABHA value
 * the server will write -- i.e. staff edited an ABHA address before saving.
 * No ABHA address (null) => nothing was replaced => never "edited".
 */
export function editedVsAbha(form, abhaValues) {
  if (!abhaValues) return false;
  return ADDRESS_FIELDS.some((field) => text(form?.[field]) !== text(abhaValues[field]));
}

// A multi-line legacy address as ONE line (F8): line breaks -> ", " (blank lines and a
// line's trailing comma dropped), runs of spaces collapsed, so words never glue together.
const oneLine = (value) => String(value ?? '')
  .split(/\r\n|\r|\n/)
  .map((line) => line.replace(/\s+/g, ' ').replace(/[\s,]+$/, '').trim())
  .filter(Boolean)
  .join(', ');

/**
 * Form-ready address of a patient row:
 *   { ...six (strings, '' when empty), address_source, legacy }
 * `address_source` is the server's read-only per-field map ({} when none).
 * `legacy` is the old one-line address text, only when all six fields are
 * empty (else null), as one line (line breaks -> ", "); that text is also seeded into `address_line` so opening
 * the block loses nothing (D5) -- the server drops the legacy line on the
 * first address_* write.
 */
export function pickAddress(patient) {
  const p = patient || {};
  const values = {};
  ADDRESS_FIELDS.forEach((field) => { values[field] = text(p[field]); });

  const legacy = ADDRESS_FIELDS.every((field) => !values[field]) ? oneLine(p.address) || null : null;
  if (legacy) values.address_line = legacy;

  const source = p.address_source && typeof p.address_source === 'object' ? { ...p.address_source } : {};
  return { ...values, address_source: source, legacy };
}
